import { inject, injectable } from "inversify";
import { TYPES } from "@utils/inversifyJS/types";
import { LibraryRepository } from "@infrastructure/repositories/LibraryRepository";
import { useUploadStore } from "@presentation/stores/UploadStore";
import axios from "axios";
import { getUploadErrorMessage } from "@presentation/common/ErrorHelpers";

const uuidv4 = () => {
  return "10000000-1000-4000-8000-100000000000".replaceAll(/[018]/g, (c: any) => (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16));
};

@injectable()
export class UploadLibraryService {
  @inject(TYPES.LibraryRepository)
  private _libraryRepository!: LibraryRepository;

  async uploadFile(file: File, projectId: string, folder?: string, rootPath?: string | null): Promise<void> {
    const id = uuidv4();
    const cancelSource = axios.CancelToken.source();
    const { addUpload, updateProgress, setComplete, setFailed, setCancelled } = useUploadStore.getState();

    // Add upload as 'library' type
    addUpload(id, file.name, folder ?? "", cancelSource, "library", file.size, undefined, projectId, rootPath);

    try {
      updateProgress(id, 0, file.size);

      await this._libraryRepository.uploadLibrary(
        file,
        projectId,
        folder,
        rootPath,
        (percent) => {
          updateProgress(id, (percent / 100) * file.size, file.size);
        },
        cancelSource.token,
      );

      setComplete(id);
    } catch (error: unknown) {
      if (axios.isCancel(error)) {
        setCancelled(id);
      } else {
        setFailed(id, getUploadErrorMessage(error));
      }
      throw error;
    }
  }

  async uploadMultipleFiles(files: File[], projectId: string, folder?: string, rootPath?: string | null): Promise<void> {
    // Simple sequential or parallel upload, similar to raw files but simpler for now
    // For libraries, maybe we don't need complex concurrency control unless many are selected
    const CONCURRENCY_LIMIT = 3;
    const queue = [...files];

    const worker = async () => {
      while (queue.length > 0) {
        const file = queue.shift();
        if (!file) break;

        try {
          await this.uploadFile(file, projectId, folder, rootPath);
        } catch (error) {
          // Continue to next file on error
          console.error("Upload failed for file", file.name, error);
        }
      }
    };

    const workers = Array.from({ length: Math.min(files.length, CONCURRENCY_LIMIT) }, () => worker());
    await Promise.all(workers);
  }
}
